import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useMarkOrderDeliveredMutation,
  useGetOrderByIdQuery,
  useDeleteOrderMutation,
} from "../slices/orderApiSlice";
import { FaArrowLeft, FaCheckCircle } from "react-icons/fa";

const VendorOrderScreen = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const {
    data: order,
    refetch,
    isLoading,
    isError,
    error,
  } = useGetOrderByIdQuery(orderId);

  const [deliverOrder, { isLoading: loadingDeliver }] =
    useMarkOrderDeliveredMutation();
  const [deleteOrder, { isLoading: loadingDelete }] =
    useDeleteOrderMutation();

  const { userInfo } = useSelector((state) => state.auth);
  const role = userInfo?.role;

  const isVendorOrDeliveryOrAdmin = ["vendor", "delivery", "admin"].includes(
    role
  );

  const formatAmount = (value) =>
    value != null ? Number(value).toFixed(2) : "0.00";

  /* ---------- Loading / Error ---------- */
  if (isLoading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "80vh" }}
      >
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          Error:{" "}
          {error?.data?.message || error.message || JSON.stringify(error)}
        </Alert>
      </Container>
    );
  }

  if (!order) return null;

  const products = order.products || [];

  /* ---------- Handlers ---------- */
  const deliverHandler = async () => {
    try {
      await deliverOrder(orderId).unwrap();
      refetch();
      alert("✅ Order marked as delivered.");
    } catch (err) {
      alert(
        err?.data?.message || err.message || "Failed to mark as delivered."
      );
    }
  };

  /* ---------- Calculations (schema-aligned) ---------- */
  const productsTotal = products.reduce(
    (sum, item) =>
      sum +
      (item.totalItemAmount ??
        (item.unitPrice || 0) * (item.quantity || 0)),
    0
  );

  const deliveryCharge = order.deliveryCharge || 0;
  const totalDiscount = order.totalDiscount || 0;
  const totalAmount = order.totalAmount || 0;

  return (
    <Container className="mt-4">
      {/* 🔙 Back */}
      <Button
        variant="dark"
        onClick={() => navigate("/Home")}
        className="mb-3"
      >
        <FaArrowLeft className="me-2" /> Back to Home
      </Button>

      <h2 className="mb-4">Order #{order._id}</h2>

      {/* 🚚 Shipping Info */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <h5>Shipping Info</h5>
          <p>
            <strong>Name:</strong> {order.customer?.name || "N/A"}
          </p>
          <p>
            <strong>Email:</strong> {order.customer?.email || "N/A"}
          </p>
          <p>
            <strong>Address:</strong>{" "}
            {order.shippingAddress?.address},{" "}
            {order.shippingAddress?.area},{" "}
            {order.shippingAddress?.city} -{" "}
            {order.shippingAddress?.pincode}
          </p>

          <p
            className={
              order.status === "Delivered"
                ? "text-success fw-semibold"
                : "text-danger fw-semibold"
            }
          >
            {order.status}
          </p>
        </Card.Body>
      </Card>

      {/* 📦 Order Items */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <h5>Order Items</h5>

          {products.length === 0 ? (
            <p className="text-muted">Order is empty</p>
          ) : (
            <Table bordered hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {products.map((item, index) => {
                  const itemTotal =
                    item.totalItemAmount ??
                    (item.unitPrice || 0) * (item.quantity || 0);

                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.name || `Product ${index + 1}`}</td>
                      <td>{item.quantity || 0}</td>
                      <td>₹{formatAmount(item.unitPrice)}</td>
                      <td>₹{formatAmount(itemTotal)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* 🧾 Order Summary */}
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <h5>Order Summary</h5>

          <Row className="mb-2">
            <Col>Products Total:</Col>
            <Col className="text-end fw-semibold">
              ₹{formatAmount(productsTotal)}
            </Col>
          </Row>

          <Row className="mb-2">
            <Col>Delivery Charge:</Col>
            <Col className="text-end fw-semibold">
              ₹{formatAmount(deliveryCharge)}
            </Col>
          </Row>

          <Row className="mb-2">
            <Col>Total Discount:</Col>
            <Col className="text-end fw-semibold text-success">
              -₹{formatAmount(totalDiscount)}
            </Col>
          </Row>

          <Row className="border-top pt-2">
            <Col className="fw-bold">Total Amount:</Col>
            <Col className="text-end fw-bold">
              ₹{formatAmount(totalAmount)}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ✅ Actions */}
      {isVendorOrDeliveryOrAdmin && (
        <Row className="justify-content-center mt-4">
          {order.status !== "Delivered" && (
            <Col xs="auto">
              <Button
                variant="success"
                onClick={deliverHandler}
                disabled={loadingDeliver}
                className="d-flex align-items-center gap-2"
              >
                {loadingDeliver ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaCheckCircle /> Mark Delivered
                  </>
                )}
              </Button>
            </Col>
          )}

          {order.status === "Pending" && (
            <Col xs="auto">
              <Button
                variant="danger"
                disabled={loadingDelete}
                onClick={async () => {
                  if (
                    window.confirm(
                      "Are you sure you want to delete this order?"
                    )
                  ) {
                    try {
                      await deleteOrder(orderId).unwrap();
                      alert("🗑️ Order deleted successfully.");
                      navigate("/Home");
                    } catch (err) {
                      alert(
                        err?.data?.message ||
                          err.message ||
                          "Failed to delete order."
                      );
                    }
                  }
                }}
              >
                {loadingDelete ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Delete Order"
                )}
              </Button>
            </Col>
          )}
        </Row>
      )}
    </Container>
  );
};

export default VendorOrderScreen;
